<template>
    <div class="card">
        <div v-if="$slots.header" class="card-header header-elements-inline">
            <slot name="header"></slot>
        </div>
        <div v-if="$slots.default" class="card-body">
            <slot></slot>
        </div>
        <div class="table-responsive" v-bind:class="{ 'loading': busy_loading }">
            <table class="table table-striped table-xs">
                <thead>
                <tr>
                    <th v-for="(col, c_index) in columns" :key="c_index">
                        <div v-if="col.sortable" @click="setSortBy(col.data)" style="cursor: pointer;" class="d-flex flex-row justify-content-between">
                            <div v-html="col.header"></div>
                            <div class="div-table-th-sort">
                                <i v-if="sort_by_column===col.data && sort_by_order==='asc'" class="icon-arrow-up7"></i>
                                <i v-else-if="sort_by_column===col.data && sort_by_order==='desc'" class="icon-arrow-down7"></i>
                                <i v-else class="inactive icon-arrow-resize8"></i>
                            </div>
                        </div>
                        <div v-else>
                            {{ col.header }}
                        </div>
                    </th>
                </tr>
                </thead>
                <tbody v-if="data.data && data.data.length>0">
                <tr v-for="(row, r_index) in data.data" :key="r_index">
                    <td v-for="(col, c_index) in columns" :key="c_index" @click="rowClick(row)" v-bind:class="{ 'cursor-pointer': has_row_click_listener }">
                        <span v-if="col.render && typeof col.render === 'function'" v-html="col.render(row)"></span>
                        <span v-else>{{ (row[col.data] ? row[col.data] : '') }}</span>
                    </td>
                </tr>
                </tbody>
                <tbody v-else>
                <tr>
                    <td :colspan="columns.length">
                        <div class="p-4 text-center"><i>Ingen data...</i></div>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
        <div class="card-footer bg-white">
            <div v-if="data.meta" class="d-flex flex-column flex-md-row align-items-center">
                <div class="text-center text-md-left">
                    <p class="mb-0">Visar {{ data.meta.from }} - {{ data.meta.to }} av {{ data.meta.total }} rader</p>
                </div>
                <div class="text-center text-md-right ml-md-auto mb-0">
                    <nav>
                        <ul class="pagination justify-content-center">
                            <li class="page-item" v-bind:class="{disabled:page<=1}">
                                <a class="page-link" @click.prevent="btnSearchPreviousPage" href="#">←   Föregående</a>
                            </li>
                            <li class="page-item active">
                                <span class="page-link">Sida {{ page }} av {{ data.meta.last_page }}</span>
                            </li>
                            <li class="page-item" v-bind:class="{disabled:page>=data.meta.last_page}">
                                <a class="page-link" @click.prevent="btnSearchNextPage" href="#">Nästa   →</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import { debounce } from 'lodash';

export default {
    name: 'RednaxDatatable',
    props: {
        url: {
            type: String,
            required: true,
        },
        columns: {
            type: Array,
            required: true,
        },
        filter: {
            type: Object,
            default() {
                return {};
            },
        },
        perPage: {
            type: Number,
            default: 25,
        },
        sortBy: {
            type: String,
            default: '',
        },
        sortOrder: {
            type: String,
            default: '',
        },
    },
    watch: {
        filter: {
            deep: true,
            handler(filter) {
                this.local_filter = filter;
                this.page = 1;
                this.get_data(true);
            },
        },
        local_filter: {
            deep: true,
            handler(filter) {
                if (this.history) {
                    this.$emit('updateFilter', filter);
                }
            },
        },
    },
    data: () => ({
        busy_loading: false,
        data: [],
        local_filter: {},
        page: 1,
        sort_by_column: '',
        sort_by_order: '',
        sort_clicktimer: null,
        sort_clicks: 0,
        get_data: debounce(function getdata(setHistory = false) {
            const vm = this;
            vm.busy_loading = true;
            if (setHistory) {
                this.setSearchHistory();
            }
            apiClient.get(`${this.url}?page=${this.page}&per_page=${this.perPage}&sort_by_column=${this.sort_by_column}&sort_by_order=${this.sort_by_order}`, { params: this.local_filter })
                .then((response) => {
                    vm.data = response.data;
                })
                .finally(() => {
                    vm.busy_loading = false;
                });
        }, 250),
    }),
    mounted() {
        this.local_filter = this.filter;
        if (this.history) {
            this.getUrlParams();
        }
        this.get_data();
    },
    created() {
        const vm = this;
        if (this.history) {
            window.onpopstate = function onpopstate(event) {
                Object.keys(event.state).forEach((i) => {
                    if (vm.local_filter[i] !== undefined) {
                        vm.local_filter[i] = event.state[i];
                    } else if (i === 'sort_by_column') {
                        vm.sort_by_column = event.state[i];
                    } else if (i === 'sort_by_order') {
                        vm.sort_by_order = event.state[i];
                    } else if (i === 'page') {
                        vm.page = parseInt(event.state[i], 10);
                    }
                });
                vm.get_data();
            };
        } else {
            this.sort_by_column = this.sortBy;
            this.sort_by_order = this.sortOrder;
        }
    },
    destroyed() {
        if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
    },
    methods: {
        getUrlParams() {
            const vm = this;
            let hasParams = false;
            window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
                if (vm.local_filter[key] !== undefined) {
                    vm.local_filter[key] = decodeURIComponent(value);
                    hasParams = true;
                } else if (key === 'sort_by_column') {
                    vm.sort_by_column = decodeURIComponent(value);
                } else if (key === 'sort_by_order') {
                    vm.sort_by_order = decodeURIComponent(value);
                } else if (key === 'page') {
                    vm.page = parseInt(value, 10);
                }
            });
            return hasParams;
        },
        setSearchHistory() {
            if (this.history) {
                const filter = {
                    ...this.local_filter, page: this.page, sort_by_column: this.sort_by_column, sort_by_order: this.sort_by_order,
                };
                const urlParams = Object.keys(filter).map((key) => `${key}=${encodeURIComponent(filter[key])}`).join('&');
                // eslint-disable-next-line no-restricted-globals
                history.pushState(filter, 'title 1', `?${urlParams}`);
            }
        },
        btnSearchPreviousPage() {
            this.page -= 1;
            this.get_data(true);
            // var elmnt = document.getElementById("header");
            // document.documentElement.scrollTop = elmnt.offsetHeight;
        },
        btnSearchNextPage() {
            this.page += 1;
            this.get_data(true);
            // var elmnt = document.getElementById("header");
            // document.documentElement.scrollTop = elmnt.offsetHeight;
        },
        setSortBy(column) {
            if (this.sort_by_column === column) {
                this.sort_clicks += 1;
                if (this.sort_clicks > 1) {
                    if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                    this.sort_by_column = '';
                    this.sort_by_order = '';
                    this.sort_clicks = 0;
                } else {
                    if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                    this.sort_clicktimer = setTimeout(() => {
                        this.sort_clicks = 0;
                    }, 2000);
                    if (this.sort_by_order === 'asc') {
                        this.sort_by_order = 'desc';
                    } else {
                        this.sort_by_order = 'asc';
                    }
                }
            } else {
                if (this.sort_clicktimer) clearTimeout(this.sort_clicktimer);
                this.sort_by_column = column;
                this.sort_by_order = 'asc';
                this.sort_clicks = 0;
            }
            this.page = 1;
            this.get_data(true);
        },
        rowClick(row) {
            if (this.has_row_click_listener) {
                this.$emit('rowclick', row);
            }
        },
        reload() {
            this.get_data();
        },
    },
    computed: {
        has_row_click_listener() {
            return this.$listeners && this.$listeners.rowclick;
        },
        history() {
            return this.$listeners && this.$listeners.updateFilter;
        },
    },
};
</script>
<style scoped>
.table-responsive.loading {
    position: relative;
}
.table-responsive.loading:before {
    content: 'Laddar...';
    display: block;
    position: absolute;
    text-align: center;
    padding: 30px 10px;
    height: 100%;
    width: 100%;
    background-color: rgba(255,255,255, .8);
}
</style>
